import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { COMPONENTS_CODE } from '../assets/ts/data/common-metadata'
import { DueOutProcessFlowMetadata } from '../assets/ts/data/dueout/dueout-process-flow-metadata'
import { InquiryProcessFlowMetadata } from '../assets/ts/data/inquiry/inquiry-process-flow-metadata'
import useDueoutProcessFlowMetadata from '../hooks/dueout/use-dueout-process-flow-metadata'
import useInquiryProcessFlowMetadata from '../hooks/inquiry/use-inquiry-process-flow-metadata'
import { USER_ROLES } from '../utils/constants/global-constants'
import { MetadataContext, MetadataContextInterface } from './metadata-context-provider'
import { UserContext } from './user-context-provider'

interface InitialFilter {
  assignedComponentCodeFilter: number[]
  cssPASCODES: string[]
  majcomCodeFilter: number[]
  sectionCodeFilter: number[]
  servicingOrganizationFilter: string[]
  servicingOrganizationWithoutSectionFilter: string[]
}
interface RoleDetails {
  allMPF: boolean
  firstLevelPermission: boolean
  fourthLevelPermission: boolean
  initialInquiryAndDueoutFilter: InitialFilter
  isAFRCOwner: boolean
  isANGOwner: boolean
  isAssignedComponentOwner: boolean
  isCPOwner: boolean
  isCSS: boolean
  isCustomer: boolean
  isHigherMPF: boolean
  isLeadership: boolean
  isLowerMPF: boolean
  isMAJCOMOwner: boolean
  isMPF: boolean
  isMPFOwner: boolean
  isRegAFOwner: boolean
  isSectionChief: boolean
  isSectionOwner: boolean
  isSiteAdmin: boolean
  isSupervisor: boolean
  isTechnician: boolean
  mpfSection: string[]
  roleIsLoading: boolean
  secondLevelPermission: boolean
  servincingComponent: string
  thirdLevelPermission: boolean
  userGroups: string[]
  userProcessFlows: DueOutProcessFlowMetadata[] | InquiryProcessFlowMetadata[]
  userType: string[]
}
const INITIAL_FILTER: InitialFilter = {
  assignedComponentCodeFilter: [],
  cssPASCODES: [],
  majcomCodeFilter: [],
  sectionCodeFilter: [],
  servicingOrganizationFilter: [],
  servicingOrganizationWithoutSectionFilter: [],
}

const roleDetailsInitalValue: RoleDetails = {
  allMPF: false,
  firstLevelPermission: false,
  fourthLevelPermission: false,
  initialInquiryAndDueoutFilter: INITIAL_FILTER,
  isAFRCOwner: false,
  isANGOwner: false,
  isAssignedComponentOwner: false,
  isCPOwner: false,
  isCSS: false,
  isCustomer: false,
  isHigherMPF: false,
  isLeadership: false,
  isLowerMPF: false,
  isMAJCOMOwner: false,
  isMPF: false,
  isMPFOwner: false,
  isRegAFOwner: false,
  isSectionChief: false,
  isSectionOwner: false,
  isSiteAdmin: false,
  isSupervisor: false,
  isTechnician: false,
  mpfSection: [],
  roleIsLoading: true,
  secondLevelPermission: false,
  servincingComponent: '',
  thirdLevelPermission: false,
  userGroups: [],
  userProcessFlows: [],
  userType: [],
}

const RoleContext = createContext(roleDetailsInitalValue)

const RoleContextProvider: React.FC = ({ children }) => {
  const [isAssignedComponentOwner, setIsAssignedComponentOwner] = useState<boolean>(false)
  const [isMPFOwner, setIsMPFOwner] = useState<boolean>(false)
  const [isCPOwner, setIsCPOwner] = useState<boolean>(false)
  const [isCSS, setIsCSS] = useState<boolean>(false)
  const [isCustomer, setIsCustomer] = useState<boolean>(false)
  const [isLeadership, setIsLeadership] = useState<boolean>(false)
  const [isMPF, setIsMPF] = useState<boolean>(false)
  const [isSectionChief, setIsSectionChief] = useState<boolean>(false)
  const [isSectionOwner, setIsSectionOwner] = useState<boolean>(false)
  const [isSiteAdmin, setIsSiteAdmin] = useState<boolean>(false)
  const [isSupervisor, setIsSupervisor] = useState<boolean>(false)
  const [isTechnician, setIsTechnician] = useState<boolean>(false)
  const [userGroups, setUserGroups] = useState<string[]>([])
  const [mpfSection, setMpfSections] = useState<string[]>([])
  const [isRegAFOwner, setIsRegAFOwner] = useState<boolean>(false)
  const [isAFRCOwner, setIsAFRCOwner] = useState<boolean>(false)
  const [isANGOwner, setIsANGOwner] = useState<boolean>(false)
  const [userProcessFlows, setUserProcessFlows] = useState<InquiryProcessFlowMetadata[]>([])
  const [servincingComponent, setSevicingComponent] = useState<string>('')
  const [roleIsLoading, setRoleIsLoading] = useState(true)
  const [userType, setUserType] = useState<string[]>([])
  const [isMAJCOMOwner, setIsMAJCOMOwner] = useState<boolean>(false)
  const [initialInquiryAndDueoutFilter, setInitialInquiryAndDueoutFilter] =
    useState<InitialFilter>(INITIAL_FILTER)
  const [fetchMetadata, setFetchMetadata] = useState<boolean>(false)

  const { me } = useContext(UserContext)
  const metadataContext = useContext(MetadataContext)
  const majcomAndCodes = metadataContext.majcoms.map((item) => ({
    code: item.MAJCOMCode,
    majcom: item.MAJCOM + ' MAJCOM Owner',
    parentComponent: item.ParentComponentCode,
  }))

  const { fetchinquiryProcessFlowMetadata, inquiryProcessFlowMetadata,
    inquiryProcessFlowMetadataIsLoading
   } =
    useInquiryProcessFlowMetadata({
      isArchivedFilter: null,
    })
  const { dueoutProcessFlowMetadata, fetchDueoutProcessFlowMetadata } = 
    useDueoutProcessFlowMetadata({
      isArchivedFilter: null,
    })

  // Use `useMemo` to optimize the `setUserRoles` function
  const setUserRoles = useMemo(() => {
    return (siteUserGroups) => {
      const userSections: string[] = []
      const mpfSections: string[] = []
      const userType: string[] = []

      if (siteUserGroups.length > 0) {
        const majcomCodes = [] as number[]
        const sectionCodes = [] as number[]
        const organizationCodes = [] as string[]
        siteUserGroups?.forEach(function (currentRole) {
          const ownersRole = ['CP Owner', 'RegAF Owner', 'ANG Owner', 'AFRC Owner']
          let squadronName: string
          let roleName: string
          if (currentRole.includes('-Owner')) {
            //Section Owner Block
            const sectionName: string[] = currentRole.split('-')[2]
            const role: string[] = currentRole.split('-')
            squadronName = role[0]
            const result = getSectionIdFromField(sectionName, 'ShortName')

            if (typeof result === 'object' && result !== null && 'secName' in result) {
              const { secCode, secName } = result
              userSections.push(secName)
              mpfSections.push(secName)
              sectionCodes.push(secCode)
              organizationCodes.push(squadronName.split(' ')[0] + ' MPF')
              setInitialInquiryAndDueoutFilter((prevState) => ({
                ...prevState,
                sectionCodeFilter: sectionCodes,
                servicingOrganizationFilter: organizationCodes,
              }))
            }
            setIsMPF(true)
            setUserServicingComponent(squadronName, metadataContext)
            setIsSectionOwner(true)
            extractDataTypeProcessFlow(
              roleName,
              mpfSections,
              sectionName,
              userSections,
              dueoutProcessFlowMetadata,
              inquiryProcessFlowMetadata,
              userType
            )
          } else if (currentRole.includes('Owner')) {
            if (ownersRole.includes(currentRole)) {
              roleName = USER_ROLES.SiteAdmin
              userType.push(USER_ROLES.SiteAdmin)
              const sectionName = ''
              switch (currentRole) {
                case 'CP Owner':
                  setIsCPOwner(true)
                  setInitialInquiryAndDueoutFilter((prevState) => ({
                    ...prevState,
                    assignedComponentCodeFilter: [],
                    sectionCodeFilter: [],
                    servicingOrganizationFilter: [],
                  }))
                  extractDataTypeProcessFlow(
                    roleName,
                    mpfSections,
                    sectionName,
                    userSections,
                    dueoutProcessFlowMetadata,
                    inquiryProcessFlowMetadata,
                    userType
                  )
                  break
                case 'RegAF Owner':
                  setIsRegAFOwner(true)
                  setIsAssignedComponentOwner(true)
                  setSevicingComponent('Regular Air Force')
                  setInitialInquiryAndDueoutFilter((prevState) => ({
                    ...prevState,
                    assignedComponentCodeFilter: cleanArray([
                      ...prevState.assignedComponentCodeFilter,
                      143,
                    ]),
                  }))
                  extractCustomerDataTypeProcessFlow(
                    dueoutProcessFlowMetadata,
                    inquiryProcessFlowMetadata
                  )
                  break
                case 'ANG Owner':
                  setIsANGOwner(true)
                  setIsAssignedComponentOwner(true)
                  setSevicingComponent('Air National Guard')
                  setInitialInquiryAndDueoutFilter((prevState) => ({
                    ...prevState,
                    assignedComponentCodeFilter: cleanArray([
                      ...prevState.assignedComponentCodeFilter,
                      145,
                    ]),
                  }))
                  extractCustomerDataTypeProcessFlow(
                    dueoutProcessFlowMetadata,
                    inquiryProcessFlowMetadata
                  )
                  break
                case 'AFRC Owner':
                  setIsAFRCOwner(true)
                  setIsAssignedComponentOwner(true)
                  setSevicingComponent('Air Force Reserve Command')
                  setInitialInquiryAndDueoutFilter((prevState) => ({
                    ...prevState,
                    assignedComponentCodeFilter: cleanArray([
                      ...prevState.assignedComponentCodeFilter,
                      144,
                    ]),
                  }))
                  extractCustomerDataTypeProcessFlow(
                    dueoutProcessFlowMetadata,
                    inquiryProcessFlowMetadata
                  )
                  break
                default:
                  break
              }
              setIsMPF(true)
            } else if (majcomAndCodes.some((item) => item.majcom.includes(currentRole))) {
              // MAJCOM Owner block
              setIsMAJCOMOwner(true)
              setIsMPF(true)
              const code =
                majcomAndCodes.find((item) => item.majcom.includes(currentRole))?.code || 1
              majcomCodes.push(code)
              const parentCode = majcomAndCodes.find((item) => item.code === code)?.parentComponent
              const component =
                parentCode === COMPONENTS_CODE.Regular_Air_Force
                  ? 'Regular Air Force'
                  : parentCode === COMPONENTS_CODE.Air_Force_Reserve_Command
                  ? 'Air Force Reserve Command'
                  : 'Air National Guard'
              setSevicingComponent(component)
              setInitialInquiryAndDueoutFilter((prevState) => ({
                ...prevState,
                majcomCodeFilter: cleanArray([...majcomCodes, code]),
              }))
              extractCustomerDataTypeProcessFlow(
                dueoutProcessFlowMetadata,
                inquiryProcessFlowMetadata
              )
            } else {
              if (metadataContext.sections.length > 0) {
                // MPF Owner Block
                roleName = USER_ROLES.SiteAdmin
                userType.push(USER_ROLES.SiteAdmin)
                const sectionName = ''
                const role: string[] = currentRole.split('-')
                squadronName = role[0]
                setIsMPFOwner(true)
                setIsMPF(true)
                setUserServicingComponent(squadronName, metadataContext)
                setInitialInquiryAndDueoutFilter((prevState) => ({
                  ...prevState,
                  servicingOrganizationWithoutSectionFilter: cleanArray([
                    ...prevState.servicingOrganizationWithoutSectionFilter,
                    squadronName?.split(' ')[0] + ' MPF',
                  ]),
                }))

                extractDataTypeProcessFlow(
                  roleName,
                  mpfSections,
                  sectionName,
                  userSections,
                  dueoutProcessFlowMetadata,
                  inquiryProcessFlowMetadata,
                  userType
                )
              }
            }
          } else if (currentRole.includes(USER_ROLES.Leadership)) {
            //Leadership Block
            const role: string[] = currentRole.split('-')
            squadronName = role[0]
            currentRole = roleName = USER_ROLES.Leadership
            const sectionName = ''
            setUserServicingComponent(squadronName, metadataContext)
            userType.push(USER_ROLES.Leadership)
            setIsLeadership(true)
            setInitialInquiryAndDueoutFilter((prevState) => ({
              ...prevState,
              servicingOrganizationWithoutSectionFilter: cleanArray([
                ...prevState.servicingOrganizationWithoutSectionFilter,
                squadronName.split(' ')[0] + ' MPF',
              ]),
            }))
            extractDataTypeProcessFlow(
              roleName,
              mpfSections,
              sectionName,
              userSections,
              dueoutProcessFlowMetadata,
              inquiryProcessFlowMetadata,
              userType
            )
          } else {
            // CSS to Section Owner
            const role: string[] = currentRole.split('-')
            squadronName = role[0]
            const sectionName = role[2]
            roleName = role[3]
            const result = getSectionIdFromField(sectionName, 'ShortName')

            if (
              ownersRole.every((role) => !siteUserGroups.includes(role)) &&
              typeof result === 'object' &&
              result !== null &&
              'secName' in result
            ) {
              const { secCode, secName } = result
              userSections.push(secName)
              mpfSections.push(secName)
              sectionCodes.push(secCode)
              organizationCodes.push(squadronName.split(' ')[0] + ' MPF')
              setInitialInquiryAndDueoutFilter((prevState) => ({
                ...prevState,
                sectionCodeFilter: sectionCodes,
                servicingOrganizationFilter: organizationCodes,
              }))
            }
            const csspascode: string[] =
              role[3] && role[3] == USER_ROLES.CSS && role[2] ? addCSSPascodes(role[2]) : []
            if (csspascode.length > 0) {
              setInitialInquiryAndDueoutFilter((prevState) => ({
                ...prevState,
                cssPASCODES: cleanArray([...prevState.cssPASCODES, ...csspascode]),
              }))
            }

            // Determine user's component
            setUserServicingComponent(squadronName, metadataContext)
            const orgNumber = squadronName.split(' ')[0]
            const componentCode = metadataContext.pascodeMetadata.find(
              (e) => e.ServicingOrganization === `${orgNumber} MPF`
            )
            userType.push(roleName)
            const componentValue = metadataContext.components
              .filter((e) => {
                return e.ComponentCode === componentCode?.AssignedComponentCode
              })
              .map((a) => a.Component)
            setSevicingComponent(componentValue[0])
            extractDataTypeProcessFlow(
              roleName,
              mpfSections,
              sectionName,
              userSections,
              dueoutProcessFlowMetadata,
              inquiryProcessFlowMetadata,
              userType
            )

            const isCSSRole = roleName ? roleName === USER_ROLES.CSS : false

            setIsCSS(isCSSRole)
          }
        })
        setRoleIsLoading(false)
      } else {
        extractCustomerDataTypeProcessFlow(dueoutProcessFlowMetadata, inquiryProcessFlowMetadata)
        setIsCustomer(true)
        setRoleIsLoading(false)
      }
    }
  }, [
    metadataContext.sections,
    metadataContext.pascodeMetadata,
    dueoutProcessFlowMetadata,
    inquiryProcessFlowMetadata,
  ])

  /**
   * Takes a CSS pascode from the user group name (last 4 digits of the PASCODE) and returns the full pascode.
   *
   * @param {string} cssPascode data that was returned from the server/api call.
   * @return {string} PASCODE data found during comparing the cssPascode with the last 4 digits of the service base metadata.
   */
  function addCSSPascodes(cssPascode) {
    const cssPASCODES: string[] = []
    metadataContext.pascodeMetadata.forEach((item) => {
      const trimmedPascode = item.PASCODE.substring(4)
      if (trimmedPascode == cssPascode) {
        const additionalPascodes = item.AdditionalPASCODEsSupported?.split(',')
        cssPASCODES.push(item.PASCODE)
        additionalPascodes?.forEach((additionalPascode) => {
          cssPASCODES.push(additionalPascode.replace(/\s/g, ''))
        })
      }
    })
    return cssPASCODES
  }
  function cleanArray(arr) {
    return arr.filter((item, index) => {
      return (
        item !== '' &&
        item !== undefined &&
        item !== null &&
        arr.indexOf(item) === index &&
        item !== -1
      )
    })
  }
  function getSectionIdFromField(value, field) {
    const obj = metadataContext.sections.find((item) => item?.[field] === value)
    return obj ? { secCode: obj.SectionCode, secName: obj.Label } : ''
  }
  const setUserServicingComponent = (squadron: string, metadata: MetadataContextInterface) => {
    const orgNumber = squadron.split(' ')[0]
    const componentCode = metadata.pascodeMetadata.find(
      (e) => e.ServicingOrganization === `${orgNumber} MPF`
    )
    const componentValue = metadata.components
      .filter((e) => {
        return e.ComponentCode === componentCode?.AssignedComponentCode
      })
      .map((a) => a.Component)
    setSevicingComponent(componentValue[0])
  }
  function setRoleObj(role) {
    switch (role) {
      case USER_ROLES.SiteAdmin:
        if (role === USER_ROLES.SiteAdmin && userGroups.includes('Admin')) {
          setIsCPOwner(true)
          setIsMPF(true)
        }
        break
      case USER_ROLES.CPOwner:
          setIsCPOwner(true)
        break
      case USER_ROLES.Leadership:
        setIsLeadership(true)
        setIsMPF(true)
        break
      case USER_ROLES.SectionChief:
        setIsMPF(true)
        setIsSectionChief(true)
        break
      case USER_ROLES.Supervisor:
        setIsMPF(true)
        setIsSupervisor(true)
        break
      case USER_ROLES.Technician:
        setIsMPF(true)
        setIsTechnician(true)
        break
      case USER_ROLES.CSS:
        setIsCSS(true)
        break
    }
  }

  /**
   *
   * @param args roleName : The role of the user example Technician
   * @param args squadronName This is used in filtering Leadership's process flow
   * @param args mpfSections : This is used to track user's section
   * @param args CurentRole : This is the user's role that is used to get in filtering specific flow
   * @param args ProcessFlowMetadata : This is the actual ProcessFlowMetadata for inquiry and due out.
   * @returns User specific process flow for both inquiry and due outs
   */
const extractDataTypeProcessFlow = (
    roleName,
    mpfSections,
    sectionName,
    userSections,
    dueoutProcessFlow,
    inquiryProcessFlow,
    userTypes
  ) => {
    const dataFlow = dueoutProcessFlow.concat(inquiryProcessFlow)
 
    const userdataFlow = dataFlow.filter((flow) => {
      return userTypes.includes(flow.UserType) || flow.UserType === 'Customer'
    })
 
    // if statement to avoid adding duplicate CSS process flows
    let sectionId = ''
    userdataFlow.forEach(function (processFlow: InquiryProcessFlowMetadata) {
      if (roleName && roleName == processFlow.UserType) {
        // Full Control (Site Admin and CP Owner)
        if (roleName === USER_ROLES.SiteAdmin) {
          sectionId = ''
          mpfSections.push('')
        } else {
          // if the user is part of a section then add that section to the userSections array
          const result = getSectionIdFromField(sectionName, 'ShortName')
 
          if (typeof result === 'object' && result !== null && 'secName' in result) {
            sectionId = result.secName
          }
 
          // push valid section from MPF users onto a section array
          if (!userSections.includes(sectionId)) {
            userSections.push(sectionId)
          }
 
          // if this is for a non CSS Role then its for full control or an MPF user so we give the MPF section permissions
          if (!mpfSections.includes(sectionId) && processFlow.UserType != USER_ROLES.CSS) {
            mpfSections.push(sectionId)
          }
        }
        userProcessFlows.push(processFlow)
        setRoleObj(processFlow.UserType)
      }
    })
 
    setUserType(userTypes)
    setMpfSections(mpfSections)
    setUserProcessFlows(userdataFlow)
  }

  /**
   * @param args metadata : This is the actual ProcessFlowMetadata for inquiry and due out.
   * @returns User specific process flow for both inquiry and due outs
   */
  const extractCustomerDataTypeProcessFlow = (dueoutProcessFlow, inquiryProcessFlow) => {
    const dataFlow = dueoutProcessFlow.concat(inquiryProcessFlow)
    const userdataFlow = dataFlow.filter((flow) => {
      return flow.UserType === 'Customer'
    })
    setUserProcessFlows(userdataFlow)
  }

  useEffect(() => {
    if (me && fetchMetadata) {
      fetchDueoutProcessFlowMetadata()
      fetchinquiryProcessFlowMetadata()
      setFetchMetadata(false)
    }
  }, [me])

  useEffect(() => {
    if (me && !metadataContext.isLoading && !inquiryProcessFlowMetadataIsLoading) {
      // give the user their userProcessFlows for their role based on comparing user groups and process flow metadata
      const allRoles = me?.roles.slice(0)
      const siteUserGroups = allRoles?.filter(
        (role: string) => !role.includes('User') && !role.includes('AppUser')
      )
      setUserGroups(siteUserGroups)

      if (me?.roles.includes('Admin')) {
        setIsSiteAdmin(true)
        setIsCPOwner(true)
      }
      if (siteUserGroups) {
        setRoleIsLoading(true)
        setUserRoles(siteUserGroups)
      }
    }
  }, [
    me,
    metadataContext.sections,
    metadataContext.pascodeMetadata,
    inquiryProcessFlowMetadataIsLoading,
    setUserRoles, // Add setUserRoles to the dependency array
  ])
  const cpOwner = isAssignedComponentOwner || isCPOwner || isMPFOwner || isSiteAdmin
  const sectionOwner =
    isAssignedComponentOwner ||
    isMAJCOMOwner ||
    isCPOwner ||
    isMPFOwner ||
    isSectionOwner ||
    isSiteAdmin
  const leadership =
    isAssignedComponentOwner ||
    isMAJCOMOwner ||
    isCPOwner ||
    isLeadership ||
    isMPFOwner ||
    isSiteAdmin
  const sectionChief =
    isAssignedComponentOwner ||
    isMAJCOMOwner ||
    isCPOwner ||
    isLeadership ||
    isMPFOwner ||
    isSectionChief ||
    isSectionOwner ||
    isSiteAdmin

  const firstLevelPermission = cpOwner
  const secondLevelPermission = firstLevelPermission || sectionOwner
  const thirdLevelPermission = firstLevelPermission || leadership
  const fourthLevelPermission = thirdLevelPermission || sectionChief

  const isLowerMPF = isCSS || isSupervisor || isTechnician
  const isHigherMPF =
    isAssignedComponentOwner ||
    isMAJCOMOwner ||
    isCPOwner ||
    isLeadership ||
    isMPFOwner ||
    isSectionChief ||
    isSectionOwner ||
    isSiteAdmin
  const allMPF = isLowerMPF || isHigherMPF

  return (
    <RoleContext.Provider
      value={{
        allMPF,
        firstLevelPermission,
        fourthLevelPermission,
        initialInquiryAndDueoutFilter,
        isAFRCOwner,
        isANGOwner,
        isAssignedComponentOwner,
        isCPOwner,
        isCSS,
        isCustomer,
        isHigherMPF,
        isLeadership,
        isLowerMPF,
        isMAJCOMOwner,
        isMPF,
        isMPFOwner,
        isRegAFOwner,
        isSectionChief,
        isSectionOwner,
        isSiteAdmin,
        isSupervisor,
        isTechnician,
        mpfSection,
        roleIsLoading,
        secondLevelPermission,
        servincingComponent,
        thirdLevelPermission,
        userGroups,
        userProcessFlows,
        userType,
      }}
    >
      {children}
    </RoleContext.Provider>
  )
}

export { RoleContext, RoleContextProvider }
